import React, { useState, useEffect, useRef } from 'react';
import Modal from '@legacy-vfuk/core-modal';
import TwoButtonModalTemplate from '@legacy-vfuk/core-two-button-modal-template';
import LoadingSpinner from '@legacy-vfuk/core-loading-spinner';
import ShowError from '../../ShowError';
import '../../css/SurveyPage.css';
import { carouselCSS } from '../../css/BusinessQuestions.styles';
import {
  KOA_API_ENDPOINT,
  OPCO_CODE,
  LANG_CODE,
} from '../SectionAssessmentBusinessGoals/SectionAssessmentBusinessGoals';
import { navigate } from 'gatsby';
import FirstQuestion from './FirstQuestion';
import SecondQuestion from './SecondQuestion';
import BusinessQuestions from './BusinessQuestions';
import CategoryQuestions from './CategoryQuestions';
import SubCategoryQuestion from './SubCategoryQuestion';
import {
  AboutBusinessContentType,
  CategoryQuestionTypes,
  FirstSetQuestionsTypes,
  FirstSetQuestionsArrayTypes,
  SubCategoryQuestionTypes,
  ParmsTypes,
} from '../../Types/SectionAssessmentAboutBusiness.types';
import twinxClient from '../../../SectionSmartSearch/ProtectedApiClient';
import { ErrorDataTypes } from '../../Types/ShowError.types';
import { ModalContentWrapper } from '../../styles/SectionAssessmentAboutBusiness.styles';
import { OverlayProvider } from '@legacy-vfuk/core-overlay-controller';
import { AssessmentSlider } from '../../AssessmentSlider/AssessmentSlider';
import { AssessmentSliderRef } from '../../AssessmentSlider/types/AssessmentSlider.types';
import fireAssessmentCommonEvents from '../../../../../../helpers/fireAssessmentCommonEvents';
import usePrevious from '../../../../../../hooks/usePrevious';
import fireStepChangeEvent from '../../../../../../helpers/fireStepChangeEvent';

const scrollToRef = ref =>
  window.scrollTo({ top: ref?.current?.offsetTop - 200 });

const SectionAssessmentAboutBusiness = ({
  content,
}: AboutBusinessContentType) => {
  const [categoryQuestion, setCategoryQuestion] = useState<
    CategoryQuestionTypes[]
  >([]);
  const [businessName, setBusinessName] = useState<string>('');
  const [loader, setLoader] = useState<boolean>(false);
  const [surveyId, setSurveyId] = useState<string>('');
  const [firstSetQuestions, setFirstSetQuestions] = useState<
    FirstSetQuestionsTypes[]
  >([]);
  const [firstSetQuestionsArray, setFirstSetQuestionsArray] = useState<
    FirstSetQuestionsArrayTypes[]
  >([]);
  const [subCategoryQuestion, setSubCategoryQuestion] =
    useState<SubCategoryQuestionTypes | null>(null);
  const [businessSection, setBusinessSection] = useState<boolean>(false);
  const [errorApiCallName, setErrorApiCallName] = useState<string>('');
  const [parms, setParms] = useState<ParmsTypes[]>([]);
  const [error, setError] = useState<ErrorDataTypes | null>(null);
  const [nextButton, setNextButton] = useState<HTMLButtonElement | null>(null);
  const [nextButtonDisabled, setNextButtonDisabled] = useState<boolean>(true);
  const [warningModalOpen, setWarningModalOpen] = useState<boolean>(false);
  const [activeSlide, setActiveSlide] = useState<number>(0);
  const carouselTop = useRef<HTMLDivElement>(null);
  const [category, setCategory] = useState<string>('');
  const [slideChangeExecutedManually, setSlideChangeExecutedManually] =
    useState<boolean>(false);
  const executeScroll = () => scrollToRef(carouselTop);
  let firstSet = false;
  const defaultAppearance = 'alt1';
  const sliderRef = useRef<AssessmentSliderRef | null>(null);
  const prevSlide = usePrevious(activeSlide);

  const regEx = /^(tertiary|none|transparent)$/;
  if (
    content.carouselLabels.backButtonPopup &&
    regEx.test(content.carouselLabels.backButtonPopup.navigateButton.rank)
  ) {
    content.carouselLabels.backButtonPopup.navigateButton.rank =
      defaultAppearance;
  }
  if (
    content.carouselLabels.backButtonPopup &&
    regEx.test(content.carouselLabels.backButtonPopup.stayButton.rank)
  ) {
    content.carouselLabels.backButtonPopup.stayButton.rank = defaultAppearance;
  }

  const nextEvent = (id: string, value: string, subCategoryId?: string) => {
    // Category Questions
    if (firstSetQuestions.length + 1 === parseInt(id)) {
      setCategory(value);
      setSubCategoryQuestion(
        categoryQuestion.filter(q => {
          return q.catergory_name === value;
        })[0]
      );
    }

    // Sub Category Questions
    if (firstSetQuestions.length + 2 === parseInt(id)) {
      const subcategory = value;
      const aboutYourBusinessAnswers = {
        direction: 'inbound',
        reason: 'Customer has answered the initial questionnaire',
        status: 'Completed',
        interactionItem: [
          {
            item: {
              category: [
                {
                  name: category,
                  subCategory: [
                    {
                      name: subcategory,
                    },
                  ],
                },
              ],
              businessQuestion: firstSetQuestionsArray,
            },
          },
        ],
        relatedParty: [
          {
            name: businessName,
            role: 'customer',
          },
          {
            name: businessName,
            role: 'user',
          },
        ],
      };
      const parmsForError: any = [subcategory, { ...aboutYourBusinessAnswers }];
      setParms(parmsForError);
      patchBusinessAnswersAPI(surveyId, subcategory, aboutYourBusinessAnswers);
    }

    setFirstSetQuestionsArray(
      firstSetQuestionsArray.map(obj => {
        return obj.id === id
          ? { ...obj, response: { responseText: [value] } }
          : obj;
      })
    );

    goToNextSlide();

    executeScroll();
  };

  const patchBusinessAnswersAPI = async (
    surveyid: string,
    subcategory: string,
    body: any
  ) => {
    setLoader(true);
    const query: any = {
      Opco: OPCO_CODE,
      Language: LANG_CODE,
      surveyid: surveyid,
    };
    const params = new URLSearchParams(query).toString();
    const url = `${KOA_API_ENDPOINT}/assessment?${params}`;
    try {
      const { data } = await twinxClient.patch(url, body);
      if (!data?.result?.hasOwnProperty('code')) {
        navigate(content.businessGoalsPageSlug, {
          state: {
            surveyId: surveyid,
            twinid: data?.result?.interactionItem[0]?.item?.id,
            subCategory: subcategory,
            businessName: businessName,
            categorySelected: category,
          },
        });
      } else {
        console.log(
          'There is some problem in patching the business related answers'
        );
        setLoader(false);
        setBusinessSection(false);
        setErrorApiCallName('patchBusinessAnswersAPI');
        setError({
          errorContent: content.serverError,
          errorType: 'serverError',
        });
      }
    } catch (error) {
      console.log(error);
      setErrorApiCallName('patchBusinessAnswersAPI');
      apiErrorHandler();
    }
  };

  const getBusinessQuestionsApi = async () => {
    setLoader(true);
    const body = {
      direction: 'outbound',
      reason: 'Customer wants to take an assessment',
      interactionItem: [
        {
          item: {
            category: [
              {
                name: 'finding & keeping customers',
                subCategory: [
                  {
                    name: 'finding & keeping customers',
                  },
                ],
              },
            ],
          },
        },
      ],
    };
    const query: any = {
      Opco: OPCO_CODE,
      Language: LANG_CODE,
    };
    const params = new URLSearchParams(query).toString();

    const businessQuestionsUrl = `${KOA_API_ENDPOINT}/assessment?${params}`;
    const categoryQuestionsUrl = `${KOA_API_ENDPOINT}/category?${params}`;
    try {
      const res = await Promise.all([
        twinxClient.post(businessQuestionsUrl, body),
        twinxClient.get(categoryQuestionsUrl),
      ]);

      const data = res.map(res => res.data);

      if (
        !data[0]?.result?.hasOwnProperty('code') &&
        !data[1]?.result?.hasOwnProperty('code')
      ) {
        setSurveyId(data[0].result.id);
        setFirstSetQuestions(
          data[0]?.result?.interactionItem[0]?.item?.question
        );
        setCategoryQuestion(data[1].result);
        setBusinessSection(true);
        setLoader(false);
      } else {
        setBusinessSection(false);
        setLoader(false);
        setErrorApiCallName('getBusinessQuestionsApi');
        setError({
          errorContent: content.serverError,
          errorType: 'serverError',
        });
      }
    } catch (error) {
      console.log(error);
      setErrorApiCallName('getBusinessQuestionsApi');
      apiErrorHandler();
    }
  };

  const apiErrorHandler = () => {
    setBusinessSection(false);
    setLoader(false);
    if (!window.navigator.onLine) {
      setError({
        errorContent: content.noInternetError,
        errorType: 'noInternetError',
      });
    } else {
      setError({ errorContent: content.serverError, errorType: 'serverError' });
    }
  };

  const createFirstSetQuestionsArray = () => {
    const arr: FirstSetQuestionsArrayTypes[] = [];
    if (
      firstSetQuestions &&
      firstSetQuestions.length > 0 &&
      firstSetQuestionsArray.length === 0 &&
      !firstSet
    ) {
      firstSetQuestions.map((item: any) =>
        arr.push({
          id: item.id,
          response: {
            responseText: [],
          },
        })
      );
      firstSet = true;
      setFirstSetQuestionsArray(arr);
    }
  };

  const firstQuesNxtBtn = () => {
    createFirstSetQuestionsArray();
    executeScroll();
  };

  const inputOnchange = (e: { target: HTMLInputElement }) => {
    const businessNameLimit = 40;
    const text = e.target.value.slice(0, businessNameLimit);
    const trimmedText = text.trim().split(/ +/).join(' ');
    setBusinessName(trimmedText !== '' ? text : '');
  };

  const getQuestionImage = (quesNum: number) => {
    const questionImages = content?.surveyQuestionsImage?.questionImages;
    const defaultImages =
      content?.surveyQuestionsImage?.defaultImage?.file?.url;
    if (questionImages && questionImages[quesNum]) {
      return questionImages[quesNum].file.url;
    } else {
      return defaultImages;
    }
  };

  const businessQuestions =
    firstSetQuestions &&
    firstSetQuestions.length > 0 &&
    firstSetQuestions.map((item: FirstSetQuestionsTypes, index: number) => {
      if (item.id === '1') {
        return (
          <SecondQuestion
            key={index}
            content={content}
            index={index}
            item={item}
            firstsetquestions={firstSetQuestions}
            getQuestionImage={getQuestionImage}
            setCreateFirstSetQuestionsArray={setFirstSetQuestionsArray}
            nextEvent={nextEvent}
          />
        );
      } else if (item.id !== '1') {
        return (
          <BusinessQuestions
            key={index}
            content={content}
            index={index}
            item={item}
            firstsetquestions={firstSetQuestions}
            selectedResponse={
              firstSetQuestionsArray[index]?.response.responseText[0]
            }
            getQuestionImage={getQuestionImage}
            nextEvent={nextEvent}
          />
        );
      }
    });

  const customParam = () => {
    if (errorApiCallName === 'getBusinessQuestionsApi') {
      getBusinessQuestionsApi();
    } else if (errorApiCallName === 'patchBusinessAnswersAPI') {
      patchBusinessAnswersAPI(surveyId, parms[0].toString(), parms[1]);
    }
  };

  useEffect(() => {
    getBusinessQuestionsApi();
    const handleKeyPress = (event: any) => {
      if (
        ['ArrowLeft', 'ArrowRight', 'Tab'].indexOf(event.key) > -1 ||
        event.keyCode === 13 ||
        event.which === 13
      ) {
        const activeEle = document.activeElement as HTMLElement;
        activeEle.blur();
        event.stopImmediatePropagation();
        event.stopPropagation();
        event.preventDefault();
        return false;
      }
    };
    document.addEventListener('keydown', handleKeyPress, true);
  }, []);

  useEffect(() => {
    let timerPrev: NodeJS.Timeout;
    if (!loader && activeSlide === 0) {
      timerPrev = setTimeout(() => {
        const foundPrevButton = document.querySelector(
          '.slick-prev'
        ) as HTMLButtonElement;
        if (foundPrevButton) {
          foundPrevButton.onclick = () => setWarningModalOpen(true);
        }
      });
    }

    return () => {
      clearTimeout(timerPrev);
    };
  }, [loader, activeSlide]);

  useEffect(() => {
    let timerNext: NodeJS.Timeout;
    if (!loader) {
      setTimeout(() => {
        const foundNextButton = document.querySelector(
          '.slick-next'
        ) as HTMLButtonElement;
        if (foundNextButton) {
          setNextButton(foundNextButton);
        }
      });
    }

    return () => {
      clearTimeout(timerNext);
    };
  }, [loader]);

  useEffect(() => {
    if (prevSlide !== undefined) {
      // because the initial page view event is already being handled by the Tealium Page Change Script
      fireStepChangeEvent();
    }
  }, [activeSlide]);

  const onSlideChange = (current: number, next: number) => {
    if (current === 0 && next === 1) {
      firstQuesNxtBtn();
    }
    if (!slideChangeExecutedManually) {
      fireAssessmentCommonEvents(
        next > current ? 'right arrow' : 'left arrow',
        'pointer click'
      );
    } else {
      setSlideChangeExecutedManually(false);
    }
    setActiveSlide(next);
  };

  useEffect(() => {
    if (slideChangeExecutedManually) {
      sliderRef.current?.nextSlide();
    }
  }, [slideChangeExecutedManually]);

  const goToNextSlide = () => {
    setSlideChangeExecutedManually(true);
  };

  useEffect(() => {
    enableDisableNextButton();
  }, [nextButton, nextButtonDisabled]);

  const enableDisableNextButton = () => {
    if (nextButton) {
      if (nextButtonDisabled) {
        nextButton.onclick = ev => {
          ev.preventDefault();
          ev.stopPropagation();
        };
        nextButton.style.cursor = 'not-allowed';
        nextButton.style.opacity = '0.25';
      } else {
        nextButton.onclick = () => {};
        nextButton.style.cursor = 'pointer';
        nextButton.style.opacity = '1';
      }
    }
  };

  useEffect(() => {
    setNextButtonDisabled(
      (activeSlide === 0 && businessName === '') || // First Question
        (activeSlide === firstSetQuestions.length + 1 && category === '') || // Category Questions
        (activeSlide > 0 && // Second Question & Business Questions
          activeSlide < firstSetQuestions.length + 1 &&
          !firstSetQuestionsArray[activeSlide - 1]?.response.responseText[0])
    );
  }, [activeSlide, businessName]);

  if (loader) {
    return (
      <div className="spring">
        <div
          className="carouselParent"
          style={{ height: '28em', display: 'grid', placeContent: 'center' }}
        >
          <AssessmentSlider>
            <LoadingSpinner appearance="secondary" size={5} />
          </AssessmentSlider>
        </div>
      </div>
    );
  } else if (businessSection) {
    return (
      <div>
        <OverlayProvider>
          <Modal
            srName="warning-modal"
            isOpen={warningModalOpen}
            size={4}
            animate
            isClosable={false}
          >
            <ModalContentWrapper>
              <img
                src={content.carouselLabels.backButtonPopup.topImage.file.url}
              ></img>
              <TwoButtonModalTemplate
                heading={{
                  text: content.carouselLabels.backButtonPopup.description,
                }}
                primaryButton={{
                  appearance:
                    content.carouselLabels.backButtonPopup.stayButton.rank,
                  text: content.carouselLabels.backButtonPopup.stayButton.label,
                  onClick: () => {
                    setWarningModalOpen(false);
                  },
                }}
                secondaryButton={{
                  appearance:
                    content.carouselLabels.backButtonPopup.navigateButton.rank,
                  text: content.carouselLabels.backButtonPopup.navigateButton
                    .label,
                  onClick: () => {
                    navigate(-1);
                  },
                }}
              />
            </ModalContentWrapper>
          </Modal>
        </OverlayProvider>

        <div className="carouselParent" css={carouselCSS} ref={carouselTop}>
          <div className="spring">
            <AssessmentSlider ref={sliderRef} beforeChange={onSlideChange}>
              <FirstQuestion
                content={content}
                firstsetquestions={firstSetQuestions}
                businessName={businessName}
                nextButtonDisabled={nextButtonDisabled}
                getQuestionImage={getQuestionImage}
                inputOnchange={inputOnchange}
                goToNextSlide={goToNextSlide}
              />
              {businessQuestions}
              <CategoryQuestions
                content={content}
                firstsetquestions={firstSetQuestions}
                categoriequestion={categoryQuestion}
                selectedResponse={category}
                getQuestionImage={getQuestionImage}
                nextEvent={nextEvent}
              />
              <SubCategoryQuestion
                content={content}
                firstsetquestions={firstSetQuestions}
                subcategoriequestion={subCategoryQuestion}
                getQuestionImage={getQuestionImage}
                nextEvent={nextEvent}
              />
            </AssessmentSlider>
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div className="spring">
        <AssessmentSlider>
          {error && (
            <ShowError
              errorContent={error.errorContent}
              errorType={error.errorType}
              customParam={customParam}
            />
          )}
        </AssessmentSlider>
      </div>
    );
  }
};

export default SectionAssessmentAboutBusiness;
